import { getConfig, getShopId, isInitialized } from './config';
import { buildEndpoint } from './endpoint';
import { getItem, setItem } from '../utils/storage';

const CONNECTION_ID_STORAGE_PREFIX = 'presence_connection_';
const CLIENT_ERROR_DEDUPE_WINDOW_MS = 15_000;

type VisibilityStateValue = 'hidden' | 'visible' | 'prerender' | 'unloaded';
type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface StorefrontClientErrorPayload {
  endpoint: string;
  method: RequestMethod;
  message: string;
  statusCode?: number;
  source?: 'sdk';
  phase?: 'request';
  attemptCount?: number;
  responseReceived?: boolean;
  pageUrl?: string;
  requestUrl?: string;
  online?: boolean;
  visibilityState?: VisibilityStateValue;
}

function createPresenceConnectionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `spx_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function getStorefrontConnectionId(storeSlug: string): string | null {
  if (typeof window === 'undefined') return null;

  const storageKey = `${CONNECTION_ID_STORAGE_PREFIX}${storeSlug}`;
  const existing = getItem<string>(storageKey);
  if (existing && existing.trim()) {
    return existing;
  }

  const nextId = createPresenceConnectionId();
  setItem(storageKey, nextId);
  return nextId;
}

function getRecentClientErrorKey(payload: StorefrontClientErrorPayload): string {
  return [
    payload.method,
    payload.endpoint,
    payload.statusCode ?? 'none',
    payload.message.trim().toLowerCase(),
    payload.responseReceived ? 'response' : 'no-response',
  ].join('|');
}

function shouldSkipDuplicateClientError(payload: StorefrontClientErrorPayload): boolean {
  const dedupeKey = `client_error_${getRecentClientErrorKey(payload)}`;
  const now = Date.now();
  const lastSeenAt = getItem<number>(dedupeKey);
  if (typeof lastSeenAt === 'number' && now - lastSeenAt < CLIENT_ERROR_DEDUPE_WINDOW_MS) {
    return true;
  }

  setItem(dedupeKey, now);
  return false;
}

export async function reportStorefrontClientError(payload: StorefrontClientErrorPayload): Promise<void> {
  if (!isInitialized()) return;
  if (typeof window === 'undefined') return;
  if (shouldSkipDuplicateClientError(payload)) return;

  const config = getConfig();
  const shopId = getShopId();
  const endpoint = shopId
    ? buildEndpoint('/v1/storefront/shops/id/:id/ping', { id: shopId })
    : buildEndpoint('/v1/storefront/shops/:storeSlug/ping', { storeSlug: config.storeSlug });
  const body = JSON.stringify({
    event_type: 'client_error',
    referer: document.referrer || undefined,
    connection_id: getStorefrontConnectionId(config.storeSlug) ?? undefined,
    client_error: {
      source: payload.source ?? 'sdk',
      phase: payload.phase ?? 'request',
      endpoint: payload.endpoint,
      method: payload.method,
      message: payload.message,
      status_code: payload.statusCode,
      attempt_count: payload.attemptCount,
      response_received: payload.responseReceived ?? false,
      page_url: payload.pageUrl ?? window.location.href,
      request_url: payload.requestUrl,
      online: payload.online ?? (typeof navigator !== 'undefined' ? navigator.onLine : undefined),
      visibility_state:
        payload.visibilityState ??
        (typeof document !== 'undefined'
          ? (document.visibilityState as VisibilityStateValue)
          : undefined),
    },
  });

  const targetUrl = `${config.apiBaseUrl}${endpoint}`;

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const beaconBody = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(targetUrl, beaconBody)) {
        return;
      }
    }

    await fetch(targetUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Telemetry must never break storefront usage.
  }
}
